/**
 * Custom hook for managing agents
 * Provides state management and API integration for agent operations
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { agentsService } from '../services/agents-service'
import { getAccessToken } from '../utils/auth-storage'
import type {
  Agent,
  AgentQueryParams,
  AgentListState,
  AgentLoadingState,
  AgentWithStatus,
  AgentStatus,
} from '../types/agent-types'

/**
 * Hook options for customizing behavior
 */
interface UseAgentsOptions {
  /** Auto-fetch agents on mount */
  autoFetch?: boolean
  /** Initial query parameters */
  initialParams?: AgentQueryParams
  /** Polling interval in milliseconds */
  pollingInterval?: number
  /** Enable real-time status updates */
  enableStatusUpdates?: boolean
}

/**
 * Return type for useAgents hook
 */
interface UseAgentsReturn {
  /** Current agent list state */
  state: AgentListState
  /** Loading state for different operations */
  loading: AgentLoadingState
  /** Fetch agents with optional parameters */
  fetchAgents: (params?: AgentQueryParams) => Promise<void>
  /** Refresh current agents list */
  refresh: () => Promise<void>
  /** Load more agents (pagination) */
  loadMore: () => Promise<void>
  /** Search agents by query */
  search: (query: string) => void
  /** Filter agents */
  filter: (filters: AgentListState['filters']) => void
  /** Select an agent */
  selectAgent: (agent: AgentWithStatus | null) => void
  /** Reset state to initial values */
  reset: () => void
}

/**
 * Custom hook for managing agents list and operations
 */
export function useAgents(options: UseAgentsOptions = {}): UseAgentsReturn {
  const {
    autoFetch = true,
    initialParams = {},
    pollingInterval,
    enableStatusUpdates = false,
  } = options

  // Main state
  const [state, setState] = useState<AgentListState>({
    agents: [],
    pagination: null,
    loading: false,
    error: null,
    searchQuery: '',
    filters: {},
    selectedAgent: null,
  })

  // Loading state for operations
  const [loading, setLoading] = useState<AgentLoadingState>({
    loading: false,
    error: null,
    retrying: false,
  })

  // Current query parameters
  const [currentParams, setCurrentParams] = useState<AgentQueryParams>(initialParams)

  /**
   * Transforms agent to include status information
   */
  const transformAgentWithStatus = useCallback((agent: Agent): AgentWithStatus => {
    // Mock status logic - in real app this would come from WebSocket or API
    const getRandomStatus = (): AgentStatus => {
      const statuses: AgentStatus[] = ['online', 'offline', 'busy']
      return statuses[Math.floor(Math.random() * statuses.length)]
    }

    const getCapabilities = (agent: Agent): string[] => {
      const capabilities: unknown[] = []
      
      if (agent.tools) {
        capabilities.push(...agent.tools)
      }
      
      if (agent.modelConfig?.model) {
        capabilities.push(agent.modelConfig.model)
      }
      
      if (agent.agentMode) {
        capabilities.push(agent.agentMode.toLowerCase())
      }
      
      return [...new Set(capabilities)]  as string[]// Remove duplicates
    }

    return {
      ...agent,
      status: getRandomStatus(),
      lastActive: new Date(Date.now() - Math.random() * 3600000).toISOString(), // Random time within last hour
      capabilities: getCapabilities(agent),
    }
  }, [])

  /**
   * Fetches agents from API
   */
  const fetchAgents = useCallback(async (params?: AgentQueryParams): Promise<void> => {
    const queryParams = params || currentParams
    
    setLoading(prev => ({ ...prev, loading: true, error: null }))
    setState(prev => ({ ...prev, loading: true, error: null }))

    try {
      const response = await agentsService.getAgents(queryParams)
      
      if (response.success) {
        const agentsWithStatus = (response.data || []).map(transformAgentWithStatus)
        
        setState(prev => ({
          ...prev,
          agents: agentsWithStatus,
          pagination: response.pagination || null,
          loading: false,
          error: null,
        }))
        
        setLoading(prev => ({ ...prev, loading: false, error: null }))
      } else {
        throw new Error('Failed to fetch agents')
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch agents'
      
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }))
      
      setLoading(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }))
    }
  }, [currentParams, transformAgentWithStatus])

  /**
   * Refreshes the current agents list
   */
  const refresh = useCallback(async (): Promise<void> => {
    await fetchAgents(currentParams)
  }, [fetchAgents, currentParams])

  /**
   * Loads more agents for pagination
   */
  const loadMore = useCallback(async (): Promise<void> => {
    if (!state.pagination?.hasNextPage || loading.loading) {
      return
    }

    const nextPage = state.pagination?.page ? state.pagination.page + 1 : 2;
    const nextParams = {
      ...currentParams,
      offset: ((nextPage - 1) * (currentParams.limit || 20)),
    }

    setLoading(prev => ({ ...prev, loading: true }))

    try {
      const response = await agentsService.getAgents(nextParams)
      
      if (response.success) {
        const newAgentsWithStatus = (response.data || []).map(transformAgentWithStatus)
        
        setState(prev => ({
          ...prev,
          agents: [...prev.agents, ...newAgentsWithStatus],
          pagination: response.pagination || null,
        }))
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load more agents'
      setLoading(prev => ({ ...prev, error: errorMessage }))
    } finally {
      setLoading(prev => ({ ...prev, loading: false }))
    }
  }, [state.pagination, loading.loading, currentParams, transformAgentWithStatus])

  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  /**
   * Searches agents by query with debounce
   */
  const search = useCallback((query: string): void => {
    setState(prev => ({ ...prev, searchQuery: query }))
    
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    
    // Set up debounced API call
    debounceTimerRef.current = setTimeout(() => {
      const searchParams = {
        ...currentParams,
        search: query.trim() || undefined,
        offset: 0 // Reset pagination when searching
      }
      
      setCurrentParams(searchParams)
      fetchAgents(searchParams)
    }, 300) // 300ms debounce delay
  }, [currentParams, fetchAgents])

  /**
   * Filters agents by specified criteria
   */
  const filter = useCallback((filters: AgentListState['filters']): void => {
    setState(prev => ({ ...prev, filters }))
    
    const newParams = {
      ...currentParams,
      published: filters.published,
      offset: 0, // Reset pagination when filtering
    }
    
    setCurrentParams(newParams)
    fetchAgents(newParams)
  }, [currentParams, fetchAgents])

  /**
   * Selects an agent
   */
  const selectAgent = useCallback((agent: AgentWithStatus | null): void => {
    setState(prev => ({ ...prev, selectedAgent: agent }))
  }, [])

  /**
   * Resets state to initial values
   */
  const reset = useCallback((): void => {
    setState({
      agents: [],
      pagination: null,
      loading: false,
      error: null,
      searchQuery: '',
      filters: {},
      selectedAgent: null,
    })
    
    setLoading({
      loading: false,
      error: null,
      retrying: false,
    })
    
    setCurrentParams(initialParams)
  }, [initialParams])

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchAgents(initialParams)
    }
  }, []) // Only run on mount

  // Polling for real-time updates
  useEffect(() => {
    if (!pollingInterval) return

    const interval = setInterval(() => {
      if (!loading.loading) {
        refresh()
      }
    }, pollingInterval)

    return () => clearInterval(interval)
  }, [pollingInterval, loading.loading, refresh])

  // Status updates simulation (in real app, this would be WebSocket)
  useEffect(() => {
    if (!enableStatusUpdates) return

    const interval = setInterval(() => {
      setState(prev => ({
        ...prev,
        agents: prev.agents.map(agent => ({
          ...agent,
          status: Math.random() > 0.8 
            ? (['online', 'offline', 'busy'] as AgentStatus[])[Math.floor(Math.random() * 3)]
            : agent.status,
        })),
      }))
    }, 10000) // Update every 10 seconds

    return () => clearInterval(interval)
  }, [enableStatusUpdates])

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return {
    state,
    loading,
    fetchAgents,
    refresh,
    loadMore,
    search,
    filter,
    selectAgent,
    reset,
  }
}

/**
 * Hook for fetching a single agent
 * Uses public endpoint for channel users, authenticated endpoint for admin users
 */
export function useAgent(agentId: string | null) {
  const [agent, setAgent] = useState<Agent | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchAgent = useCallback(async (id: string) => {
    setLoading(true)
    setError(null)

    try {
      // Guard: Skip fetch when no auth token (prevents request loops while logged out)
      if (!getAccessToken()) {
        setError('Not authenticated')
        setLoading(false)
        return
      }

      // Suppress the api-client error toast: the standalone agent page renders
      // its own "no access / not found" screen, so a toast (fired once per
      // request, and twice under StrictMode's double-invoke) is redundant noise.
      const response = await agentsService.getAgent(id, { showErrorToast: false })

      if (response.success) {
        setAgent(response.data)
      } else {
        throw new Error('Failed to fetch agent')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch agent'
      setError(errorMessage)
      setAgent(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (agentId) {
      fetchAgent(agentId)
    } else {
      setAgent(null)
      setError(null)
    }
  }, [agentId, fetchAgent])

  return {
    agent,
    loading,
    error,
    refetch: agentId ? () => fetchAgent(agentId) : () => {},
  }
}